import { Component, DestroyRef, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ISegmentRule } from '../../../../types/contactModule';
import { GroupAudienceService } from '../../../../services/segments/group-audience.service';
import { describeRule, draftRulesToCriteria, isCompleteRule } from '../segment-utilities';
import { formatCount } from '../../../../../utils/sms';

type PreviewState = 'empty' | 'loading' | 'ready' | 'error';

/**
 * "About N contacts match": counts the contact list with the conditions
 * as they stand, a moment after the last change. The saved segment's exact
 * count comes from the contacts service once it is saved.
 */
@Component({
  selector: 'segment-preview',
  standalone: true,
  imports: [],
  templateUrl: './segment-preview.component.html',
  styleUrls: ['../segment-builder.css', './segment-preview.component.css'],
})
export class SegmentPreviewComponent implements OnInit, OnChanges {
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);

  @Input() rules: ISegmentRule[] = [];

  readonly formatCount = formatCount;

  state: PreviewState = 'empty';
  count: number | null = null;
  skipped = 0;
  sentence = '';
  /** Read out once per settled count, not on every keystroke. */
  announcement = '';

  private changes = new Subject<ISegmentRule[]>();

  ngOnInit() {
    this.changes
      .pipe(
        map((rules) => {
          const complete = (rules ?? []).filter(isCompleteRule);
          return { complete, criteria: draftRulesToCriteria(complete) };
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a.criteria) === JSON.stringify(b.criteria)),
        debounceTime(450),
        switchMap(({ complete, criteria }) => {
          if (!complete.length) {
            return of({ state: 'empty' as PreviewState, count: null, skipped: 0 });
          }
          this.state = 'loading';
          return this.audience.countContacts(criteria.params).pipe(
            map((count) => ({ state: 'ready' as PreviewState, count, skipped: criteria.skipped })),
            catchError(() => of({ state: 'error' as PreviewState, count: null, skipped: criteria.skipped }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.state = result.state;
        this.count = result.count;
        this.skipped = result.skipped;
        this.announcement =
          result.state === 'ready' && result.count !== null
            ? `About ${formatCount(result.count)} ${result.count === 1 ? 'contact matches' : 'contacts match'}.`
            : result.state === 'error'
              ? 'The estimate did not load.'
              : '';
      });
    this.update();
  }

  ngOnChanges() {
    this.update();
  }

  private update() {
    const complete = (this.rules ?? []).filter(isCompleteRule);
    const parts = complete.map((rule) => {
      const text = describeRule(rule.attributeName, rule.rule ?? '', (rule.values?.[0] ?? '').toString());
      return text.charAt(0).toLowerCase() + text.slice(1);
    });
    this.sentence = parts.length ? `Contacts whose ${parts.join(' and ')}.` : '';
    if (!complete.length) this.state = 'empty';
    this.changes.next(this.rules ?? []);
  }

  get skippedNote(): string {
    if (!this.skipped) return '';
    return this.skipped === 1
      ? 'The estimate leaves out 1 condition it cannot count, so the real number may be lower.'
      : `The estimate leaves out ${this.skipped} conditions it cannot count, so the real number may be lower.`;
  }
}
